// Shared by the browser and function SDKs. Never collect payloads in telemetry.
const PROJECTED_CONTEXTS = new Set([
    'request_data',
    'netlify_event',
    'full_error',
    'api_response',
    'originalError',
]);
const DIAGNOSTIC_FIELDS = new Set([
    'userId',
    'customerId',
    'subscriptionId',
    'siteId',
    'planId',
    'productId',
    'priceId',
    'stripePrice',
    'productGroup',
    'isTrial',
    'hasBillingContact',
    'hasInvoiceDetails',
    'forceUpgrade',
    'prorationDate',
    'method',
    'httpMethod',
    'path',
    'status',
    'statusCode',
    'code',
    'errorType',
    'name',
    'message',
    'type',
    'contentType',
    'sentryEventId',
    'bodyLength',
    'decline_code',
]);

/**
 * Retain allowlisted diagnostic fields without collecting request payloads.
 * @param {unknown} value
 * @param {number} [depth]
 * @returns {object}
 */
function projectDiagnostics(value, depth = 0) {
    if (!value || typeof value !== 'object' || depth > 5) return {};
    return Object.fromEntries(
        Object.entries(value).flatMap(([key, entry]) => {
            if (key === 'headers' && entry && typeof entry === 'object') {
                return [
                    [
                        key,
                        Object.fromEntries(
                            Object.entries(entry)
                                .filter(
                                    ([name, value]) =>
                                        ['content-type', 'user-agent'].includes(name) &&
                                        typeof value === 'string',
                                )
                                .map(([name, value]) => [name, sanitizeText(value)]),
                        ),
                    ],
                ];
            }
            if (key === 'addonIds' && Array.isArray(entry))
                return [
                    [
                        key,
                        entry
                            .filter(
                                (id) => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(id),
                            )
                            .slice(0, 50),
                    ],
                ];
            if (
                ['data', 'error', 'originalError'].includes(key) &&
                entry &&
                typeof entry === 'object'
            )
                return [[key, projectDiagnostics(entry, depth + 1)]];
            if (
                DIAGNOSTIC_FIELDS.has(key) &&
                ['string', 'number', 'boolean'].includes(typeof entry)
            )
                return [[key, typeof entry === 'string' ? sanitizeText(entry) : entry]];
            return [];
        }),
    );
}

const PRIVATE_KEY =
    /^(jwt|token|.*token.*|.*secret.*|password|.*authorization.*|.*cookie.*|headers|body|data|originalError|full_error|api_response|netlify_event|request_data|vars|email|http\.(?:query|fragment)|(?:user\.)?ip_address)$/i;

/**
 * Remove URL queries and recognizable credentials from diagnostic strings.
 * @param {string} value
 * @returns {string}
 */
export function sanitizeText(value) {
    return (
        value
            .replace(/\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]+\b/g, '[Filtered]')
            .replace(/\bBearer\s+[^\s"']+/gi, 'Bearer [Filtered]')
            .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[Filtered]')
            .replace(/https?:\/\/[^\s"'<>]+/g, (url) => url.split(/[?#]/)[0])
            // Also match paths without queries so failed matches cannot rescan every slash.
            .replace(/([/?][^\s?#]*)(?:\?[^\s]*)?/g, '$1')
    );
}

/**
 * Scrub events, breadcrumbs and spans before transport. Preserve structural
 * `data` on spans/breadcrumbs, but never raw request/response data or bodies.
 * @template T
 * @param {T} value
 * @returns {T}
 */
export function sanitizeTelemetry(value) {
    const ancestors = new WeakSet();
    /**
     * @param {unknown} item
     * @param {number} [depth]
     * @param {string} [parentKey]
     * @returns {unknown}
     */
    const walk = (item, depth = 0, parentKey = '') => {
        if (depth > 20) return '[Filtered]';
        if (typeof item === 'string') return sanitizeText(item);
        if (!item || typeof item !== 'object') return item;
        if (ancestors.has(item)) return '[Filtered]';
        ancestors.add(item);
        try {
            if (Array.isArray(item)) {
                return item
                    .filter((entry) => entry?.category !== 'console')
                    .map((entry) => walk(entry, depth + 1));
            }
            return Object.fromEntries(
                Object.entries(item)
                    .filter(
                        ([key]) =>
                            PROJECTED_CONTEXTS.has(key) ||
                            (parentKey === 'user' && depth === 1 && key === 'email') ||
                            !PRIVATE_KEY.test(key) ||
                            (key === 'data' &&
                                ('category' in item ||
                                    'op' in item ||
                                    ('trace_id' in item && 'span_id' in item))),
                    )
                    .map(([key, entry]) => {
                        // Sentry consumes this metadata and removes it before transport.
                        if (depth === 0 && key === 'sdkProcessingMetadata') return [key, entry];
                        if (PROJECTED_CONTEXTS.has(key)) return [key, projectDiagnostics(entry)];
                        return [key, walk(entry, depth + 1, key)];
                    }),
            );
        } finally {
            ancestors.delete(item);
        }
    };
    return walk(value);
}
